import { useState, useRef, useEffect } from 'react';
import { API_URL } from '../../config/api';
import './bookChat.css';

const BOOK_CONFIGS = {
  inbetween: {
    cover: () => import('../../assets/ebooks/theinbetweenEbook.png'),
    title: 'The In-Between',
    author: 'Loubna Bouzenzen',
    greeting: "Hi! I'm here to help you explore *The In-Between*. Ask me anything about the book — what it covers, whether it's right for you, or how to order it. 📖",
    suggestions: [
      'Is this book for me?',
      'What will I learn?',
      'How long is the book?',
      'What is the In-Between concept?',
    ],
    colors: {
      primary: '#6e4f38',
      primaryHover: '#5a3d2b',
      primaryLight: '#fdf8f3',
      primaryBorder: 'rgba(110, 79, 56, 0.2)',
      primaryBorderLight: 'rgba(110, 79, 56, 0.08)',
      primaryBorderMedium: 'rgba(110, 79, 56, 0.12)',
      suggestionHover: '#f4ebe0',
      dotColor: '#b8956a',
      textDark: '#2c1a0e',
      scrollThumb: '#d4bfa8',
    },
  },
  bwgo: {
    cover: () => import('../../assets/beforewegetolderCover.png'),
    title: 'Before We Get Older',
    author: 'Loubna Bouzenzen',
    greeting: "Hi! I'm here to help you explore *Before We Get Older*. Ask me anything about the book — what it covers, whether it's right for you, or how to order it. 📖",
    suggestions: [
      'Is this book for me?',
      'What will I learn?',
      'How many chapters are there?',
      'What age is this book for?',
    ],
    colors: {
      primary: '#2c346b',
      primaryHover: '#1e2550',
      primaryLight: '#f3f4fb',
      primaryBorder: 'rgba(44, 52, 107, 0.2)',
      primaryBorderLight: 'rgba(44, 52, 107, 0.08)',
      primaryBorderMedium: 'rgba(44, 52, 107, 0.12)',
      suggestionHover: '#e8eaf5',
      dotColor: '#6b7bbf',
      textDark: '#0e1130',
      scrollThumb: '#a8afd4',
    },
  },
};

function BookChat({ book = 'inbetween' }) {
  const config = BOOK_CONFIGS[book];
  const { colors } = config;

  const [isOpen, setIsOpen] = useState(false);
  const [coverSrc, setCoverSrc] = useState(null);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: config.greeting },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const cssVars = {
    '--chat-primary':              colors.primary,
    '--chat-primary-hover':        colors.primaryHover,
    '--chat-primary-light':        colors.primaryLight,
    '--chat-primary-border':       colors.primaryBorder,
    '--chat-primary-border-light': colors.primaryBorderLight,
    '--chat-primary-border-medium':colors.primaryBorderMedium,
    '--chat-suggestion-hover':     colors.suggestionHover,
    '--chat-dot-color':            colors.dotColor,
    '--chat-text-dark':            colors.textDark,
    '--chat-scroll-thumb':         colors.scrollThumb,
  };

  useEffect(() => {
    config.cover().then(mod => setCoverSrc(mod.default));
  }, [book, config]);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  }, [messages, isOpen]);

  // FIX 1: Toggle open/close
  const handleToggle = () => {
    if (!isOpen) setHasOpened(true);
    setIsOpen(prev => !prev);
  };

  const sendMessage = async (text) => {
    const userText = text || input.trim();
    if (!userText || isLoading) return;

    const newMessages = [...messages, { role: 'user', content: userText }];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/chat/${book}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: newMessages }),
      });

      const data = await response.json();
      const reply = data.reply || "I'm sorry, I couldn't process that. Please try again.";
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: "Sorry, something went wrong. Please try again in a moment." },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const KNOWN_LINKS = {
    'Revolution Library': 'https://www.instagram.com/librairie_de_la_revolution_/',
    'Zahra Library': 'https://www.instagram.com/librairie_zahra_23/',
    'Crypto Library': 'https://www.instagram.com/crypto_library19/',
    'Amazon & Kindle': 'https://www.amazon.com/dp/B0BLWM4WPG',
    'Amazon listing': 'https://www.amazon.com/dp/B0BLWM4WPG',
    'Amazon': 'https://www.amazon.com/dp/B0BLWM4WPG',
    'order page': '/order',
    'Order page': '/order',
    'WhatsApp': 'https://wa.me/213654683600',
    'Gumroad': book === 'bwgo'
      ? 'https://elbylit.gumroad.com/l/kjbehu'
      : 'https://elbylit.gumroad.com/l/wafvso',
  };

  const formatText = (text) => {
    let clean = text
      .replace(/_{1,2}link_{1,2}"?\s*/gi, '')
      .replace(/_{1,2}(\w[\w\s]*?)_{1,2}/gi, '$1')
      .replace(/<a\b[^>]*>(.*?)<\/a>/gi, '$1')
      .replace(/<[^>]+>/g, '')
      .replace(/\s*target="_blank"\s*/gi, '')
      .replace(/\s*rel="noopener noreferrer"\s*/gi, '')
      .replace(/\s*style="[^"]*"\s*/gi, '')
      .replace(/\s*href="[^"]*"\s*/gi, '')
      .replace(/"{1,2}\s*>/g, '')
      .replace(/\(([^)]*)\)/g, (match, inner) => {
        if (Object.keys(KNOWN_LINKS).some(k => inner.trim() === k)) return '';
        return match;
      });

    const urls = [];
    clean = clean.replace(/(https?:\/\/[^\s<"]+)/g, (match) => {
      urls.push(match);
      return '{{URL_PLACEHOLDER_' + (urls.length - 1) + '}}';
    });

    const sortedKeys = Object.keys(KNOWN_LINKS).sort((a, b) => b.length - a.length);
    sortedKeys.forEach(label => {
      const url = KNOWN_LINKS[label];
      const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(?<![">])(${escaped})(?![^<]*<\\/a>)`, 'g');
      clean = clean.replace(
        regex,
        `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:inherit;font-weight:600;text-decoration:underline;">$1</a>`
      );
    });

    urls.forEach((url, index) => {
      clean = clean.replace('{{URL_PLACEHOLDER_' + index + '}}', '');
    });

    clean = clean
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');

    return clean;
  };

  return (
    <div className="chat-widget" style={cssVars}>
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-header-left">
              {coverSrc && (
                <img src={coverSrc} alt={config.title} className="chat-book-thumb" />
              )}
              <div>
                <p className="chat-header-title">Ask about the book</p>
                <p className="chat-header-sub">{config.title} · {config.author}</p>
              </div>
            </div>
            {/* FIX 1: header X also uses handleToggle */}
            <button className="chat-close-btn" onClick={handleToggle}>✕</button>
          </div>

          <div className="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`chat-bubble ${msg.role}`}>
                <p dangerouslySetInnerHTML={{ __html: formatText(msg.content) }} />
              </div>
            ))}

            {messages.length === 1 && (
              <div className="chat-suggestions">
                {config.suggestions.map((q, i) => (
                  <button key={i} className="suggestion-btn" onClick={() => sendMessage(q)}>
                    {q}
                  </button>
                ))}
              </div>
            )}

            {isLoading && (
              <div className="chat-bubble assistant loading">
                <span /><span /><span />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-row">
            <input
              ref={inputRef}
              type="text"
              placeholder="Ask anything about the book..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
            />
            <button
              className="chat-send-btn"
              onClick={() => sendMessage()}
              disabled={!input.trim() || isLoading}
            >
              →
            </button>
          </div>
        </div>
      )}

      {/* FIX 1: bubble button now toggles */}
      <button className={`chat-bubble-btn ${hasOpened ? '' : 'pulse'}`} onClick={handleToggle}>
        {isOpen ? '✕' : '💬'}
        {!hasOpened && <span className="chat-bubble-label">Ask about the book</span>}
      </button>
    </div>
  );
}

export default BookChat;